/**
 * Optimizer — IR optimization passes.
 *
 * Performs:
 * - Dead code elimination (unused instructions)
 * - Constant folding (literal-only arithmetic)
 * - Literal inlining (single-use literal refs)
 *
 * Works with the new structured IR (statements with control flow).
 */

import type {
  IRBlock,
  IRFor,
  IRIf,
  IRInstruction,
  IRProgram,
  IRStatement,
  IRWhile,
  PortDataType,
} from '../types';

// ============================================================================
// Main entry
// ============================================================================

/**
 * Optimize an IR program (returns a new program).
 */
export function optimizeIR(program: IRProgram, level: number): IRProgram {
  let blocks = program.blocks.map(b => ({
    name: b.name,
    statements: [...(b.statements ?? [])],
  }));

  // Level 1: basic
  if (level >= 1) {
    blocks = blocks.map(b => ({
      name: b.name,
      statements: foldConstantsInStatements(b.statements),
    }));

    blocks = blocks.map(b => ({
      name: b.name,
      statements: eliminateDeadCodeInStatements(b.statements),
    }));
  }

  // Level 2: more aggressive passes
  if (level >= 2) {
    blocks = blocks.map(b => ({
      name: b.name,
      statements: eliminateDeadCodeInStatements(b.statements),
    }));
  }

  return {
    name: program.name,
    fields: program.fields,
    blocks,
  };
}

// ============================================================================
// Statement-level operations (recursive)
// ============================================================================

/**
 * Apply constant folding to a list of statements (recursive).
 */
function foldConstantsInStatements(statements: IRStatement[]): IRStatement[] {
  const literals = new Map<string, { value: string; type: PortDataType }>();
  return statements.map(stmt => foldStatementConstants(stmt, literals));
}

function foldStatementConstants(
  stmt: IRStatement,
  literals: Map<string, { value: string; type: PortDataType }>,
): IRStatement {
  switch (stmt.kind) {
    case 'instruction':
      return foldInstructionConstants(stmt, literals);

    case 'if':
      return {
        ...stmt,
        thenBody: stmt.thenBody.map(s => foldStatementConstants(s, new Map(literals))),
        elseBody: stmt.elseBody?.map(s => foldStatementConstants(s, new Map(literals))),
      } as IRIf;

    case 'for':
      return {
        ...stmt,
        body: stmt.body.map(s => foldStatementConstants(s, new Map(literals))),
      } as IRFor;

    case 'while':
      return {
        ...stmt,
        body: stmt.body.map(s => foldStatementConstants(s, new Map(literals))),
      } as IRWhile;

    default:
      return stmt;
  }
}

function foldInstructionConstants(
  instr: IRInstruction,
  literals: Map<string, { value: string; type: PortDataType }>,
): IRInstruction {
  // Track literals
  if (instr.op === 'literal' && instr.inputs.length === 1 && instr.inputs[0].startsWith('#')) {
    literals.set(instr.id, { value: instr.inputs[0].slice(1), type: instr.resultType });
    return instr;
  }

  // Only fold op: and certain other operations
  const foldableOps = ['add', 'sub', 'mul', 'div', 'mod', 'neg', 'abs', 'floor', 'ceil', 'round', 'sqrt', 'min', 'max', 'pow'];
  const op = instr.op.replace(/^op:/, '');
  if (!foldableOps.includes(op)) return instr;

  // Check if all inputs are literal
  const resolvedInputs: { value: string; type: PortDataType }[] = [];
  let allLiteral = true;

  for (const ref of instr.inputs) {
    if (ref.startsWith('#')) {
      resolvedInputs.push({ value: ref.slice(1), type: instr.resultType });
    } else {
      const lit = literals.get(ref);
      if (lit) {
        resolvedInputs.push(lit);
      } else {
        allLiteral = false;
        break;
      }
    }
  }

  if (!allLiteral) return instr;

  // Try to evaluate
  const result = evaluateConstant(op, resolvedInputs.map(l => l.value));
  if (result !== null) {
    const folded: IRInstruction = {
      kind: 'instruction',
      id: instr.id,
      op: 'literal',
      inputs: [`#${result}`],
      resultType: instr.resultType,
      sourceNodeId: instr.sourceNodeId,
    };
    literals.set(instr.id, { value: result, type: instr.resultType });
    return folded;
  }

  return instr;
}

// ============================================================================
// Dead code elimination (recursive)
// ============================================================================

/**
 * Remove unused instructions from statements (recursive).
 */
function eliminateDeadCodeInStatements(statements: IRStatement[]): IRStatement[] {
  // Build reference counts across all statements
  const refCount = new Map<string, number>();
  countReferencesInStatements(statements, refCount);

  // Filter statements
  return filterDeadStatements(statements, refCount);
}

function countReferencesInStatements(statements: readonly IRStatement[], refCount: Map<string, number>): void {
  for (const stmt of statements) {
    countReferencesInStatement(stmt, refCount);
  }
}

function countReferencesInStatement(stmt: IRStatement, refCount: Map<string, number>): void {
  switch (stmt.kind) {
    case 'instruction':
      for (const input of stmt.inputs) {
        if (!input.startsWith('#') && !input.startsWith('$')) {
          refCount.set(input, (refCount.get(input) ?? 0) + 1);
        }
      }
      break;

    case 'if':
      if (!stmt.condition.startsWith('#') && !stmt.condition.startsWith('$')) {
        refCount.set(stmt.condition, (refCount.get(stmt.condition) ?? 0) + 1);
      }
      countReferencesInStatements(stmt.thenBody, refCount);
      if (stmt.elseBody) countReferencesInStatements(stmt.elseBody, refCount);
      break;

    case 'for':
      if (!stmt.start.startsWith('#') && !stmt.start.startsWith('$')) {
        refCount.set(stmt.start, (refCount.get(stmt.start) ?? 0) + 1);
      }
      if (!stmt.end.startsWith('#') && !stmt.end.startsWith('$')) {
        refCount.set(stmt.end, (refCount.get(stmt.end) ?? 0) + 1);
      }
      countReferencesInStatements(stmt.body, refCount);
      break;

    case 'while':
      if (!stmt.condition.startsWith('#') && !stmt.condition.startsWith('$')) {
        refCount.set(stmt.condition, (refCount.get(stmt.condition) ?? 0) + 1);
      }
      countReferencesInStatements(stmt.body, refCount);
      break;
  }
}

function filterDeadStatements(statements: IRStatement[], refCount: Map<string, number>): IRStatement[] {
  return statements
    .map(stmt => filterDeadStatement(stmt, refCount))
    .filter((stmt): stmt is IRStatement => stmt !== null);
}

function filterDeadStatement(stmt: IRStatement, refCount: Map<string, number>): IRStatement | null {
  switch (stmt.kind) {
    case 'instruction':
      // Keep if has side effects or is referenced
      if (stmt.hasSideEffects) return stmt;
      if ((refCount.get(stmt.id) ?? 0) > 0) return stmt;
      return null;

    case 'if':
      return {
        ...stmt,
        thenBody: filterDeadStatements([...stmt.thenBody], refCount),
        elseBody: stmt.elseBody ? filterDeadStatements([...stmt.elseBody], refCount) : undefined,
      } as IRIf;

    case 'for':
      return {
        ...stmt,
        body: filterDeadStatements([...stmt.body], refCount),
      } as IRFor;

    case 'while':
      return {
        ...stmt,
        body: filterDeadStatements([...stmt.body], refCount),
      } as IRWhile;

    default:
      return stmt; // break, continue, etc.
  }
}

// ============================================================================
// Constant evaluation
// ============================================================================

/**
 * Evaluate a constant operation. Returns the literal string or null.
 */
function evaluateConstant(op: string, args: string[]): string | null {
  const nums = args.map(a => parseFloat(a));
  if (nums.some(n => isNaN(n))) return null;

  let result: number;
  switch (op) {
    case 'add':   result = nums[0] + nums[1]; break;
    case 'sub':   result = nums[0] - nums[1]; break;
    case 'mul':   result = nums[0] * nums[1]; break;
    case 'div':   if (nums[1] === 0) return null; result = nums[0] / nums[1]; break;
    case 'mod':   if (nums[1] === 0) return null; result = nums[0] % nums[1]; break;
    case 'neg':   result = -nums[0]; break;
    case 'abs':   result = Math.abs(nums[0]); break;
    case 'floor': result = Math.floor(nums[0]); break;
    case 'ceil':  result = Math.ceil(nums[0]); break;
    case 'round': result = Math.round(nums[0]); break;
    case 'sqrt':  if (nums[0] < 0) return null; result = Math.sqrt(nums[0]); break;
    case 'min':   result = Math.min(nums[0], nums[1]); break;
    case 'max':   result = Math.max(nums[0], nums[1]); break;
    case 'pow':   result = Math.pow(nums[0], nums[1]); break;
    default:      return null;
  }

  // Format as literal
  if (Number.isInteger(result)) return `${result}.0`;
  return `${result}`;
}
